import { unwrap, type MemberOut } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, Icon, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { FormAlert } from "../auth/components/form-alert";
import { useErrorMessage } from "../lib/api-errors";
import { useInvalidateAccess } from "../roles/use-roles";
import { useApi } from "../session/session-provider";
import { useWorkspaceContext } from "../session/workspace-context";

interface RemoveMemberDialogProps {
  member: MemberOut;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Remove a member, or leave the workspace (own row). The API keeps at least one Owner. */
export function RemoveMemberDialog({ member, open, onOpenChange }: RemoveMemberDialogProps) {
  const { t } = useTranslation();
  const api = useApi();
  const navigate = useNavigate();
  const message = useErrorMessage();
  const invalidate = useInvalidateAccess();
  const { summary } = useWorkspaceContext();
  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/members/{membership_id}", { params: { path: { membership_id: member.id } } })),
    onSuccess: async () => {
      onOpenChange(false);
      if (member.is_you) {
        toast.success(t("team.left", { workspace: summary.name }));
        await navigate("/", { replace: true });
        return;
      }
      toast.success(t("team.removed", { name: member.name }));
      await invalidate();
    },
  });
  const title = member.is_you ? t("team.leaveTitle", { workspace: summary.name }) : t("team.removeTitle", { name: member.name });
  const body = member.is_you ? t("team.leaveBody") : t("team.removeBody", { workspace: summary.name });
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) remove.reset();
        onOpenChange(next);
      }}
    >
      <DialogContent
        title={title}
        description={body}
        closeLabel={t("common.close")}
        className="max-w-md"
        footer={
          <>
            <Button variant="secondary" onClick={() => { onOpenChange(false); }}>
              {t("team.cancel")}
            </Button>
            <Button variant="danger" loading={remove.isPending} onClick={() => { remove.mutate(); }}>
              <Icon name={member.is_you ? "log-out" : "trash"} />
              {member.is_you ? t("team.leave") : t("team.remove")}
            </Button>
          </>
        }
      >
        {remove.isError ? <FormAlert title={message(remove.error)} /> : null}
      </DialogContent>
    </Dialog>
  );
}
